// Wire types for the PayrollIntegrations Function App (camelCase JSON) and the
// AdminService GlobalConfiguration documents (PascalCase JSON — read/written
// through the explicit parsers/serializers in lib/global-config.ts; a generic
// case transform would corrupt provider wire-field names in ConstantFields).

/** Every provider the backend seam supports — the type stays two-wide. */
export const ALL_PROVIDERS = ["EmployDrive", "ADP"] as const
export type ProviderName = (typeof ALL_PROVIDERS)[number]

/**
 * Providers offered anywhere in the admin UI (Payroll systems setup, filters,
 * value translations). ADP is deliberately hidden until its integration is
 * tested end to end (MAR-19, blocked on Advantage Tech credentials) — add it
 * back here when it is; the backend adapter seam stays registered throughout.
 */
export const PROVIDERS: readonly ProviderName[] = ["EmployDrive"]

export type MigrationStatus =
  | "Pending"
  | "WaitingForWorkOrder"
  | "Sent"
  | "Created"
  | "Failed"
  | "Skipped"
  | "Ignored"
  | "Resolved"

export interface MigrationRow {
  userProfileId: number
  workerName: string
  provider: string
  providerEnvironmentName: string
  internalOrganizationId: number
  internalOrganizationCode?: string | null
  status: MigrationStatus
  trigger: string
  providerEmployeeId?: string | null
  error?: string | null
  gateReason?: string | null
  attemptCount: number
  createdOn: string
  lastAttemptedOn?: string | null
  completedOn?: string | null
}

export interface MigrationDetail extends MigrationRow {
  contactId: number
  profileTypeCode: string
  correlationId: string
  /**
   * The detail endpoint returns the raw record, whose field is
   * lastProcessingError — only the LIST endpoint aliases it to `error`.
   */
  lastProcessingError?: string | null
  requestPayload?: Record<string, unknown> | null
  /**
   * The exact body posted to the provider, in the provider's wire shape.
   * Present only once a send has been attempted — the detail panel shows this
   * and never the prepared payload, so what is displayed as "sent" is what was
   * actually sent.
   */
  providerRequestPayload?: string | null
  responsePayload?: string | null
  lastActionBy?: string | null
  resolvedBy?: string | null
  resolvedOn?: string | null
}

export interface CredentialView {
  provider: string
  providerEnvironmentName: string
  clientId?: string | null
  clientSecret?: string | null
  certificate?: string | null
  certificatePassphrase?: string | null
  lastRotatedOn?: string | null
  lastRotatedBy?: string | null
}

// ---- AdminService GlobalConfiguration documents (camelCase in-memory models;
// ---- PascalCase on the wire via lib/global-config.ts) ----

export interface CompanyMapping {
  internalOrganizationId: number
  internalOrganizationCode: string
  internalOrganizationName: string
  /** Optional per-org override of the provider-level client id. */
  providerClientId?: string | null
  /** EmployDrive legalCode for this internal org — same Legal the payment file uses. */
  legalCode?: string | null
  /** EmployDrive payGroup for this internal org — same PayGroup the payment file uses. */
  payGroup?: string | null
  isExcluded: boolean
}

export interface OrganizationFieldDefault {
  title: string
  value: string
  valueDescription?: string | null
}

export interface CategoryFieldDefaults {
  /** Merged verbatim at the top level of the employee payload; JSON types preserved. */
  constantFields: Record<string, unknown>
  /** organizations[] entries, e.g. Division = Consultant / Corp to Corp. */
  organizationFields: OrganizationFieldDefault[]
}

export interface FieldDefaultsSet {
  all: CategoryFieldDefaults
  individual: CategoryFieldDefaults
  organization: CategoryFieldDefaults
}

export interface ProviderConfigurationData {
  provider: string
  isEnabled: boolean
  providerEnvironmentName: string
  baseUrl: string
  /** Provider-side client id (one client serves the tenant; per-org mapping value overrides). */
  providerClientId?: string | null
  companyMappings: CompanyMapping[]
  allowedProfileTypes: string[]
  migrateOnApproval: boolean
  resyncOnChange: boolean
  sendTerminations: boolean
  /** Opt-in reconciliation sweep — OFF by default so enabling a provider never triggers an unsolicited full backfill. */
  reconcileMissedWorkers: boolean
  /** One provider employee per internal org (same employeeNumber, different legalCode) — OFF until Harmer/EmployDrive confirm. */
  migratePerInternalOrganization: boolean
  /** C2C last name (firstName carries the full org legal name). */
  organizationLastNameSuffix: string
  // Opt-in work-order-sourced fields (workbook: "can be left blank").
  includeHourlyRate: boolean
  includeNormalHours: boolean
  includeDepartment: boolean
  includeWorkersCompCode: boolean
  /** Per-worker-category constants (idType SSN/EIN, employmentCategoryCode FT/CtoC, Division, ...). Edited as the "Standard values" form on the Providers tab. */
  fieldDefaults: FieldDefaultsSet
}

/**
 * Profile types are LABELLED with the tenant's own code-table Values (e.g.
 * Harmer's "W2 Worker"), fetched from /lookups/profile-types. This list is
 * only the fallback when that lookup is unavailable — raw codes, never
 * invented display text.
 */
export const WORKER_PROFILE_TYPE_CODES = [
  "WorkerUnitedStatesW2",
  "WorkerUnitedStatesLLC",
  "WorkerSubVendor",
  "WorkerTemp",
  "WorkerCanadianInc",
  "WorkerCanadianSP",
] as const

export interface ProfileTypeLookup {
  code: string
  value: string
}

/** Tenant internal organization (org.OrganizationInternalRole → org.Organization). */
export interface InternalOrganizationLookup {
  id: number
  code: string
  name: string
}

/** An FBO-side value for a translatable field: value = what the mapper emits (the id as a string), label = display name. */
export interface ValueOption {
  value: string
  label: string
}

export const GROUP_NAMES = {
  paymentFileFormatType: "PaymentFileFormatType",
  providerConfiguration: "PayrollProviderConfiguration",
  valueMappingSet: "PayrollValueMappingSet",
} as const

// ---- Value translations (Mappings tab) ----

export interface ValueMappingRow {
  fboValue: string
  providerValue: string
  description?: string | null
}

export interface ValueMappingSetData {
  provider: string
  /** Which detail this list translates. Known: "department", "workersCompCode", "workLocation". */
  field: string
  mappings: ValueMappingRow[]
}

/** The translatable details and their everyday names. */
export const TRANSLATABLE_FIELDS = [
  { field: "department", label: "Department", hint: "FBO sends the job category number unless you translate it here." },
  { field: "workersCompCode", label: "Workers' comp code", hint: "FBO sends its internal code number unless you translate it here." },
  { field: "workLocation", label: "Work location", hint: "FBO sends the two-letter state (IL) unless your payroll system wants its own location codes — translate them here." },
] as const

// ---- Backfill (add existing workers) ----

export interface BackfillCandidate {
  userProfileId: number
  workerName: string
  profileTypeCode: string
  /** The tenant's display name for the type (code-table Value, e.g. "W2 Worker"). */
  profileTypeValue?: string
  typeIsSent: boolean
}
